import type { LocalizedText } from '@portal/kernel';
import { useTranslation } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['vi', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Picks the text for `language`, falling back to Vietnamese (the source language). */
export function localize(text: LocalizedText, language: string): string {
  return (language === 'en' ? text.en : undefined) ?? text.vi;
}

/** Returns a function that renders manifest/API `LocalizedText` in the current UI language. */
export function useLocalize(): (text: LocalizedText) => string {
  const { i18n } = useTranslation();
  return (text) => localize(text, i18n.resolvedLanguage ?? i18n.language);
}

/**
 * Turns an API error into a user-facing message: `errors.<problem code>` from the module's namespace,
 * falling back to its `errors.generic`.
 */
export function useProblemMessage(namespace: string): (error: unknown) => string {
  const { t } = useTranslation(namespace);
  return (error) => {
    const code =
      error && typeof error === 'object' && 'code' in error
        ? String((error as { code: unknown }).code)
        : '';
    return t(`errors.${code}`, { defaultValue: t('errors.generic') });
  };
}
